import { useI18n, type MessageKey } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Button,
  Card,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';

import { ORDERS_PAGE_SIZE, useOrdersQuery } from '../api';
import type { ManualPaymentMethod } from '../types';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { formatCatalogPrice } from '@/features/products/format-price';

const METHOD_LABEL_KEY: Record<ManualPaymentMethod, MessageKey> = {
  cod: 'merchant.manual.payment.cod',
  cash: 'merchant.manual.payment.cash',
  bank_transfer: 'merchant.manual.payment.bankTransfer',
  card: 'merchant.manual.payment.card',
  cheque: 'merchant.manual.payment.cheque',
  other: 'merchant.manual.payment.other',
};

/**
 * « Ventes » — the sales the merchant recorded by hand (`orders.source = manual_sale`, migration 0096).
 *
 * A manual sale IS an order row: it already counts in the revenue, the customers, the invoices and the order list, so
 * nothing here is added on top of those figures. This page lists them with their own payment state — « Payée » only
 * when the merchant recorded the payment as received. The two amounts below are for the sales on this page.
 */
export function SalesPage(): ReactElement {
  const { t } = useI18n();
  const [offset, setOffset] = useState(0);
  const sales = useOrdersQuery(undefined, offset, 'manual_sale');
  const page = sales.data;
  const orders = page?.orders ?? [];
  const paidAmount = orders
    .filter((order) => order.paymentStatus === 'confirmed')
    .reduce((sum, order) => sum + order.grandTotal, 0);
  const toCollectAmount = orders
    .filter((order) => order.paymentStatus !== 'confirmed' && order.status !== 'CANCELLED')
    .reduce((sum, order) => sum + order.grandTotal, 0);

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">{t('merchant.sales.title')}</h1>
          <p className="text-sm text-inkFaint">{t('merchant.sales.subtitle')}</p>
        </div>
        <Link to="/sales/new">
          <Button>{t('merchant.sales.add')}</Button>
        </Link>
      </div>

      {page !== undefined && orders.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-3">
          <Card className="p-4">
            <p className="text-xs uppercase tracking-wide text-inkSoft">
              {t('merchant.sales.count')}
            </p>
            <p className="font-display text-2xl font-bold tabular-nums text-ink">{page.total}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs uppercase tracking-wide text-inkSoft">
              {t('merchant.sales.paidAmount')}
            </p>
            <p className="font-display text-2xl font-bold tabular-nums text-good">
              {formatCatalogPrice(paidAmount)}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-xs uppercase tracking-wide text-inkSoft">
              {t('merchant.sales.toCollect')}
            </p>
            <p className="font-display text-2xl font-bold tabular-nums text-warn">
              {formatCatalogPrice(toCollectAmount)}
            </p>
          </Card>
        </div>
      ) : null}

      {sales.isPending ? (
        <TableSkeleton columns={5} />
      ) : sales.isError ? (
        <EmptyState
          title={t('merchant.sales.loadError')}
          description={t(resolveErrorMessageKey(sales.error))}
        />
      ) : page === undefined || orders.length === 0 ? (
        <EmptyState title={t('merchant.sales.empty')} description={t('merchant.sales.emptyHint')} />
      ) : (
        <>
          <Table caption={t('merchant.sales.title')}>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t('merchant.orders.table.colOrder')}</TableHeaderCell>
                <TableHeaderCell>{t('merchant.orders.table.colCustomer')}</TableHeaderCell>
                <TableHeaderCell>{t('merchant.sales.colPayment')}</TableHeaderCell>
                <TableHeaderCell>{t('merchant.orders.table.colTotal')}</TableHeaderCell>
                <TableHeaderCell>{t('merchant.sales.colDate')}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => {
                const method = order.paymentMethod as ManualPaymentMethod;
                const isPaid = order.paymentStatus === 'confirmed';
                return (
                  <TableRow key={order.id}>
                    <TableCell>
                      <span className="font-mono text-sm text-ink">#{order.orderNumber}</span>
                      <p className="text-xs text-inkFaint">
                        {order.items
                          .map((item) => `${String(item.quantity)} × ${item.productName}`)
                          .join(', ')}
                      </p>
                      {order.internalNote !== null ? (
                        <p className="text-xs italic text-inkSoft">{order.internalNote}</p>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-ink">{order.customerName}</span>
                      <p className="text-xs text-inkFaint">{order.customerPhone}</p>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                          isPaid ? 'bg-goodSoft text-good' : 'bg-warnSoft text-warn'
                        }`}
                      >
                        {t(
                          isPaid ? 'merchant.manual.paymentPaid' : 'merchant.manual.paymentPending',
                        )}
                      </span>
                      <p className="mt-1 text-xs text-inkFaint">
                        {Object.hasOwn(METHOD_LABEL_KEY, method)
                          ? t(METHOD_LABEL_KEY[method])
                          : order.paymentMethod}
                        {order.paymentReference !== null ? ` · ${order.paymentReference}` : ''}
                      </p>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm text-ink">
                        {formatCatalogPrice(order.grandTotal)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-inkFaint">
                        {new Date(order.createdAt).toLocaleString('fr-TN')}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {page.total > ORDERS_PAGE_SIZE ? (
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={offset === 0}
                onClick={() => {
                  setOffset(Math.max(0, offset - ORDERS_PAGE_SIZE));
                }}
              >
                {t('merchant.sales.previous')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={offset + orders.length >= page.total}
                onClick={() => {
                  setOffset(offset + ORDERS_PAGE_SIZE);
                }}
              >
                {t('merchant.sales.next')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
